import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { connectAppSocket } from "../config/socket";
import { SOCKET_EVENTS } from "../config/socket/events";
import { EMPTY_PREVIEW } from "../config/api/emptyResponses";

const SUPPORT_KEY = ["support-tickets"];
const UNREAD_KEY = ["support-unread"];

function ticketIdOf(payload) {
  const value = payload?.ticketId || payload?.ticket?._id || payload?.message?.ticket;
  return value ? String(value) : "";
}

function pushSupportMessage(queryClient, ticketId, message, viewing) {
  if (!ticketId || !message?._id) return;

  queryClient.setQueriesData({ queryKey: ["support-messages", ticketId] }, (current) => {
    if (!current?.docs) return current;
    if (current.docs.some((row) => String(row._id) === String(message._id))) return current;
    return {
      ...current,
      docs: [...current.docs, message],
      totalDocs: Number(current.totalDocs || current.docs.length) + 1,
    };
  });

  queryClient.setQueriesData({ queryKey: SUPPORT_KEY }, (current) => {
    if (!current?.docs) return current;
    const fromAdmin = message.senderRole === "ADMIN";
    const preview = String(message.body || "").slice(0, 160);
    const docs = current.docs.map((ticket) => {
      if (String(ticket._id) !== ticketId) return ticket;
      const unread = Number(ticket.adminUnreadCount || 0);
      return {
        ...ticket,
        lastMessageAt: message.createdAt || new Date().toISOString(),
        lastMessagePreview: preview,
        adminUnreadCount: fromAdmin || viewing ? 0 : unread + 1,
      };
    });
    docs.sort(
      (a, b) => new Date(b.lastMessageAt || 0).getTime() - new Date(a.lastMessageAt || 0).getTime()
    );
    return { ...current, docs };
  });
}

export function useSupportSocket(token, { activeTicketId } = {}) {
  const queryClient = useQueryClient();
  const activeTicketRef = useRef(activeTicketId);
  activeTicketRef.current = activeTicketId;

  useEffect(() => {
    if (!token || EMPTY_PREVIEW) return undefined;

    const socket = connectAppSocket(token);
    if (!socket) return undefined;

    const invalidateSupport = (ticketId) => {
      queryClient.invalidateQueries({ queryKey: SUPPORT_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
      if (ticketId) {
        queryClient.invalidateQueries({ queryKey: ["support-ticket", ticketId] });
        queryClient.invalidateQueries({ queryKey: ["support-messages", ticketId] });
      }
    };

    const onTicketNew = (payload) => {
      invalidateSupport(payload?.ticket?._id);
    };

    const onTicketUpdated = (payload) => {
      invalidateSupport(payload?.ticket?._id || payload?.ticketId);
    };

    const onMessageNew = (payload) => {
      const ticketId = ticketIdOf(payload);
      const viewing = ticketId && ticketId === String(activeTicketRef.current || "");
      if (payload?.message) {
        pushSupportMessage(queryClient, ticketId, payload.message, viewing);
      }
      invalidateSupport(ticketId);
    };

    socket.on(SOCKET_EVENTS.SUPPORT_TICKET_NEW, onTicketNew);
    socket.on(SOCKET_EVENTS.SUPPORT_TICKET_UPDATED, onTicketUpdated);
    socket.on(SOCKET_EVENTS.SUPPORT_MESSAGE_NEW, onMessageNew);

    return () => {
      socket.off(SOCKET_EVENTS.SUPPORT_TICKET_NEW, onTicketNew);
      socket.off(SOCKET_EVENTS.SUPPORT_TICKET_UPDATED, onTicketUpdated);
      socket.off(SOCKET_EVENTS.SUPPORT_MESSAGE_NEW, onMessageNew);
    };
  }, [token, queryClient]);

  useEffect(() => {
    if (!token || EMPTY_PREVIEW || !activeTicketId) return undefined;

    const socket = connectAppSocket(token);
    if (!socket) return undefined;

    const joinTicket = () => {
      socket.emit(
        SOCKET_EVENTS.SUPPORT_JOIN,
        { ticketId: activeTicketId },
        () => {}
      );
    };

    if (socket.connected) {
      joinTicket();
    }
    socket.on(SOCKET_EVENTS.CONNECTED, joinTicket);

    return () => {
      socket.off(SOCKET_EVENTS.CONNECTED, joinTicket);
      socket.emit(SOCKET_EVENTS.SUPPORT_LEAVE, { ticketId: activeTicketId });
    };
  }, [token, activeTicketId]);
}

export default useSupportSocket;
